import { initializeApp } from 'firebase/app';
import { getFirestore, doc, getDoc } from 'firebase/firestore';

// Environment variables to determine backend and API endpoints
const BACKEND_TYPE = process.env.REACT_APP_BACKEND_TYPE || 'FIREBASE'; // Default to FIREBASE
const AWS_API_ENDPOINT = process.env.REACT_APP_AWS_API_ENDPOINT; // e.g., https://api.stock-dev.algo-x.io

// Firebase configuration (consider moving to .env if not already)
const firebaseConfig = {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY || "AIzaSyC3ak3Ob8Km7cvZ9KJbRxGY9OPDDO2BKvE",
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || "hkstock-89b58.firebaseapp.com",
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || "hkstock-89b58",
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || "hkstock-89b58.firebasestorage.app",
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || "768875429044",
    appId: process.env.REACT_APP_FIREBASE_APP_ID || "1:768875429044:web:0dd951ac24ff06e3706be1",
    measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID || "G-5NJJW260BT"
};

let db;
if (BACKEND_TYPE === 'FIREBASE') {
    const app = initializeApp(firebaseConfig);
    db = getFirestore(app);
}

const fetchLatestDataFirebase = async () => {
    const latestDocSnap = await getDoc(doc(db, 'data', 'latest'));
    if (!latestDocSnap.exists()) {
        throw new Error("Latest document not found in Firebase.");
    }
    const latestData = latestDocSnap.data();

    const rangeDocSnap = await getDoc(doc(db, 'data', latestData.date, 'range', 'data'));
    const biasDocSnap = await getDoc(doc(db, 'data', latestData.date, 'bias', 'data'));

    if (!rangeDocSnap.exists() || !biasDocSnap.exists()) {
        throw new Error("Range or Bias data not found for the latest date in Firebase.");
    }

    return {
        latest: latestData,
        rangeData: rangeDocSnap.data(),
        biasData: biasDocSnap.data(),
    };
};

const fetchLatestDataAws = async () => {
    if (!AWS_API_ENDPOINT) {
        throw new Error("AWS API endpoint is not configured. Please set REACT_APP_AWS_API_ENDPOINT.");
    }
    // // Remove the /dev prefix if it's already in the API endpoint
    // const endpoint = AWS_API_ENDPOINT.endsWith('/dev')
    //     ? AWS_API_ENDPOINT
    //     : `${AWS_API_ENDPOINT}/dev`;
    const response = await fetch(`${AWS_API_ENDPOINT}/latest`);
    if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: response.statusText }));
        throw new Error(`Failed to fetch latest data from AWS: ${errorData.message}`);
    }
    const data = await response.json();
    // Transform AWS data to match the structure expected by App.js
    // AWS response: {'date': latest_date, 'results': items, 'latest_run_status': latest_status}
    // Expected: { latest: {date, timestamp, status, warning}, rangeData: {}, biasData: {} }

    const transformed = {
        latest: {
            date: data.date,
            timestamp: data.latest_run_status?.timestamp ? new Date(data.latest_run_status.timestamp) : new Date(), // Convert string to Date
            status: data.latest_run_status?.status,
            warning: data.latest_run_status?.warning,
            // doc_ref is firebase specific, not directly applicable
        },
        rangeData: {},
        biasData: {}
    };

    data.results.forEach(item => {
        const symbol = item.symbol;
        transformed.rangeData[symbol] = {
            last_close: item.last_close,
            last_high: item.last_high,
            last_low: item.last_low,
            lower_range: item.lower_range,
            upper_range: item.upper_range,
            atr: item.atr,
            // outside_atr_range_pct, etc. are not directly split in DynamoDB like Firestore subcollections
            // These were part of the main item in the 'results' array from analyze_stocks_for_lambda
            // For simplicity, we'll keep them flat here. The UI might need adjustment or further transformation.
            outside_atr_range_pct: item.outside_atr_range_pct,
            outside_atr_up_range_pct: item.outside_atr_up_range_pct,
            outside_atr_down_range_pct: item.outside_atr_down_range_pct,
        };
        transformed.biasData[symbol] = {
            next_day_bias_up_high: item.next_day_bias_up_high,
            next_day_bias_down_high: item.next_day_bias_down_high,
            next_day_bias_up_low: item.next_day_bias_up_low,
            next_day_bias_down_low: item.next_day_bias_down_low,
            model_accuracy_high: item.model_accuracy_high,
            model_accuracy_low: item.model_accuracy_low,
            long_name: item.long_name,
        };
    });

    return transformed;
};

export const fetchLatestStockData = async () => {
    console.log(`Fetching data using backend: ${BACKEND_TYPE}`);
    if (BACKEND_TYPE === 'AWS') {
        return fetchLatestDataAws();
    } else if (BACKEND_TYPE === 'FIREBASE') {
        return fetchLatestDataFirebase();
    } else {
        throw new Error(`Unsupported backend type: ${BACKEND_TYPE}`);
    }
};